import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import Toast from "./Toast";
import SlumpTestReportPreview from "./SlumpTestReportPreview";
import StrengthTestReportPreview from "./StrengthTestReportPreview";

interface TestRecord {
  id: string;
  name: string;
  category: "fresh" | "hardened";
  status: "completed" | "pending" | "in-progress";
  date?: string;
  values?: Record<string, any>;
  imageUrl?: string;
  description?: string;
}

interface EnhancedTestsViewProps {
  batchId: string;
  castingDate: string;
  grade?: string;
  onTestComplete?: (testData: {
    testId: string;
    testName: string;
    batchId: string;
    grade: string;
    date: string;
    values: any;
  }) => void;
  onNavigateToReports?: () => void;
}

export default function EnhancedTestsView({
  batchId,
  castingDate,
  grade = "M30",
  onTestComplete,
  onNavigateToReports,
}: EnhancedTestsViewProps) {
  const [activeCategory, setActiveCategory] = useState<"fresh" | "hardened">("fresh");
  const [expandedTest, setExpandedTest] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState<{ message: string; type: "success" | "info" | "warning" } | null>(null);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<"all" | "completed" | "pending" | "in-progress">("all");
  const [showAddTestModal, setShowAddTestModal] = useState(false);
  const [selectedTestType, setSelectedTestType] = useState<"7-day" | "14-day" | "28-day" | null>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [testFormData, setTestFormData] = useState({
    strength: "",
    cubeWeight: "",
    load: "",
    temperature: "",
    notes: "",
    cubeDimensions: "",
    testingMachine: "",
    technicianName: "",
    humidity: "",
    ageAtTest: "",
    failureMode: "",
    averageStrength: "",
  });
  const [generatedPDF, setGeneratedPDF] = useState<string | null>(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderData, setReminderData] = useState({
    testType: "",
    reminderDate: "",
    reminderTime: "",
    notes: "",
  });
  const [activeReminders, setActiveReminders] = useState<any[]>([]);
  const [showSlumpReportPreview, setShowSlumpReportPreview] = useState(false);
  const [showStrengthReportPreview, setShowStrengthReportPreview] = useState(false);
  const [selectedStrengthTest, setSelectedStrengthTest] = useState<string | null>(null);
  const [showFailureModeSheet, setShowFailureModeSheet] = useState(false);
  const [showReminderTypeSheet, setShowReminderTypeSheet] = useState(false);

  // State to track test statuses dynamically
  const [testStatuses, setTestStatuses] = useState<Record<string, "completed" | "pending" | "in-progress">>({});
  
  // Project and customer information
  const [projectInfo] = useState({
    customerName: "Skyline Developers Pvt. Ltd.",
    projectName: "Prestige Metro Towers - Phase 3",
    projectLocation: "Electronic City, Bangalore",
    contractorName: "ABC Construction Ltd.",
  });
  
  // Calculate days since casting
  const daysSinceCasting = Math.floor((new Date().getTime() - new Date(castingDate).getTime()) / (1000 * 60 * 60 * 24));
  
  // Check which tests are due
  const isTest7DayDue = daysSinceCasting >= 7 && daysSinceCasting < 14;
  const isTest14DayDue = daysSinceCasting >= 14 && daysSinceCasting < 28;
  const isTest28DayDue = daysSinceCasting >= 28;

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result as string);
        setShowToast({ message: "Test image uploaded successfully", type: "success" });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddTestClick = (testType: "7-day" | "14-day" | "28-day") => {
    setSelectedTestType(testType);
    setShowAddTestModal(true);
    setTestFormData({
      strength: "",
      cubeWeight: "",
      load: "",
      temperature: "",
      notes: "",
      cubeDimensions: "",
      testingMachine: "",
      technicianName: "",
      humidity: "",
      ageAtTest: testType === "7-day" ? "7" : testType === "14-day" ? "14" : "28",
      failureMode: "",
      averageStrength: "",
    });
    setUploadedImage(null);
  };

  const handleSetReminder = (testType: string) => {
    setReminderData({
      testType,
      reminderDate: "",
      reminderTime: "",
      notes: "",
    });
    setShowReminderModal(true);
  };

  const handleSaveReminder = () => {
    if (!reminderData.reminderDate || !reminderData.testType) {
      setShowToast({ message: "Please select test type and date", type: "warning" });
      return;
    }

    const newReminder = {
      id: Date.now().toString(),
      ...reminderData,
      createdAt: new Date().toISOString(),
    };

    setActiveReminders(prev => [...prev, newReminder]);
    setShowToast({ message: `Reminder set for ${reminderData.testType}`, type: "success" });
    setShowReminderModal(false);
  };

  const handleDeleteReminder = (reminderId: string) => {
    setActiveReminders(prev => prev.filter(r => r.id !== reminderId));
    setShowToast({ message: "Reminder deleted", type: "success" });
  };

  const handleSubmitTest = () => {
    if (!testFormData.strength || !testFormData.cubeWeight) {
      setShowToast({ message: "Please fill in required fields", type: "warning" });
      return;
    }

    // Generate PDF with test data
    const pdfData = {
      testType: selectedTestType,
      batchId,
      castingDate,
      testDate: new Date().toLocaleDateString(),
      ...testFormData,
      image: uploadedImage,
    };

    setGeneratedPDF(JSON.stringify(pdfData));
    setShowToast({ message: `${selectedTestType} test data saved! PDF generated.`, type: "success" });
    
    // Call parent callback
    if (onTestComplete) {
      onTestComplete(selectedTestType || "", pdfData);
    }

    // Close modal after short delay
    setTimeout(() => {
      setShowAddTestModal(false);
    }, 1500);
  };

  const handleDownloadPDF = () => {
    if (generatedPDF) {
      // In a real app, this would generate an actual PDF
      const blob = new Blob([generatedPDF], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${batchId}-${selectedTestType}-test-report.pdf`;
      a.click();
      setShowToast({ message: "PDF downloaded successfully", type: "success" });
    }
  };

  const handleViewPDF = () => {
    if (generatedPDF) {
      setShowToast({ message: "Opening PDF preview...", type: "info" });
      // Navigate to reports view or show PDF preview
      if (onNavigateToReports) {
        onNavigateToReports();
      }
    }
  };
  
  const handleExportTest = (testId: string) => {
    setShowToast({ message: `Exporting ${testId} data...`, type: "info" });
    setTimeout(() => {
      setShowToast({ message: "Test data exported successfully", type: "success" });
    }, 1000);
  };

  const handleRetestRequest = (testId: string) => {
    setShowToast({ message: `Retest request submitted for ${testId}`, type: "info" });
  };

  const handleShareTest = async (testId: string) => {
    // Find the test in both fresh and hardened tests
    const allTests = [...freshTests, ...hardenedTests];
    const test = allTests.find(t => t.id === testId);
    
    if (!test) {
      setShowToast({ message: "Test not found", type: "warning" });
      return;
    }

    // Get the main value
    const mainValue = test.values.slump || test.values.strength || test.values.temperature || test.values.density || test.values.airContent || test.values.absorption || test.values.reboundNumber || test.values.velocity || test.values.initialSet;
    
    // Create share data
    const shareData = {
      title: `${test.name} - ${batchId}`,
      text: `Test Result: ${mainValue} ${test.values.unit}\nStatus: ${test.values.status}\nTarget: ${test.values.target || 'N/A'}\nDate: ${test.date || castingDate}\nGrade: ${grade}`,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setShowToast({ message: "Test results shared successfully", type: "success" });
      } else {
        // Try clipboard as fallback, but handle if it's blocked
        try {
          await navigator.clipboard.writeText(`${shareData.title}\n\n${shareData.text}`);
          setShowToast({ message: "Test results copied to clipboard", type: "success" });
        } catch (clipboardError) {
          setShowToast({ message: "Share feature not available in this environment", type: "info" });
        }
      }
    } catch (error) {
      console.log("Share cancelled or failed");
    }
  };

  const handleToggleFavorite = (testId: string) => {
    setFavorites(prev => 
      prev.includes(testId) 
        ? prev.filter(id => id !== testId)
        : [...prev, testId]
    );
    setShowToast({ 
      message: favorites.includes(testId) ? "Removed from favorites" : "Added to favorites", 
      type: "success" 
    });
  };

  const handleStartTest = (testId: string) => {
    setTestStatuses(prev => ({ ...prev, [testId]: "in-progress" }));
    const allTests = [...freshTests, ...hardenedTests];
    const test = allTests.find(t => t.id === testId);
    if (test) {
      setShowToast({ message: `${test.name} started`, type: "success" });
    }
  };

  const handleCompleteTest = (testId: string) => {
    const allTests = [...freshTests, ...hardenedTests];
    const test = allTests.find(t => t.id === testId);
    
    if (!test) return;

    // Mark test as completed
    setTestStatuses(prev => ({ ...prev, [testId]: "completed" }));
    
    const currentDate = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    // Call parent callback to generate report
    if (onTestComplete) {
      onTestComplete({
        testId: test.id,
        testName: test.name,
        batchId,
        grade,
        date: currentDate,
        values: test.values || {},
      });
    }

    setShowToast({ message: `${test.name} completed! Report generated.`, type: "success" });
    setExpandedTest(null);
  };

  const handleCompareTests = () => {
    setShowToast({ message: "Opening comparison view...", type: "info" });
  };

  const handleDownloadAll = () => {
    setShowToast({ message: "Downloading all test results...", type: "info" });
    setTimeout(() => {
      setShowToast({ message: "All tests downloaded successfully", type: "success" });
    }, 1500);
  };

  const handlePrintSummary = () => {
    setShowToast({ message: "Preparing test summary for printing...", type: "info" });
    setTimeout(() => {
      setShowToast({ message: "Print dialog ready", type: "success" });
    }, 1000);
  };
  
  // Comprehensive RMC test data with realistic values
  const freshTests: TestRecord[] = [
    {
      id: "slump",
      name: "Slump Cone Test",
      category: "fresh",
      status: "completed",
      date: "Dec 23, 2024 - 09:42 AM",
      description: "Measures workability and consistency of fresh concrete",
      imageUrl: "https://images.unsplash.com/photo-1549600152-7b6e5c67f82c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMHNsdW1wJTIwY29uZSUyMHRlc3R8ZW58MXx8fHwxNzY2NDg1ODQzfDA&ixlib=rb-4.1.0&q=80&w=1080",
      values: {
        slump: 85,
        unit: "mm",
        target: "75-100",
        slumpClass: "S3",
        coneHeight: 300,
        status: "optimal",
        compliance: "IS 1199:1959"
      }
    },
    {
      id: "temperature",
      name: "Concrete Temperature",
      category: "fresh",
      status: "completed",
      date: "Dec 23, 2024 - 09:45 AM",
      description: "Critical for hydration rate and strength development",
      imageUrl: "https://images.unsplash.com/photo-1719404362503-24d06934cb2c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMHRlbXBlcmF0dXJlJTIwbWVhc3VyZW1lbnR8ZW58MXx8fHwxNzY2NDg1NDA3fDA&ixlib=rb-4.1.0&q=80&w=1080",
      values: {
        temperature: 28,
        unit: "°C",
        ambient: 31,
        max: 32,
        status: "optimal",
        compliance: "ACI 305R"
      }
    },
    {
      id: "density",
      name: "Fresh Density Test",
      category: "fresh",
      status: "completed",
      date: "Dec 23, 2024 - 09:48 AM",
      description: "Indicates proper mix proportions and compaction",
      imageUrl: "https://images.unsplash.com/photo-1633677095081-492aad9530d9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMGZyZXNoJTIwbWl4JTIwZGVuc2l0eXxlbnwxfHx8fDE3NjY0ODU0MDl8MA&ixlib=rb-4.1.0&q=80&w=1080",
      values: {
        density: 2450,
        unit: "kg/m³",
        target: "2400-2500",
        volume: 8000,
        weight: 19600,
        status: "optimal",
        compliance: "IS 1199:1959"
      }
    },
    {
      id: "air-content",
      name: "Air Content Test",
      category: "fresh",
      status: "completed",
      date: "Dec 23, 2024 - 09:52 AM",
      description: "Measures entrained air for freeze-thaw resistance",
      imageUrl: "https://images.unsplash.com/photo-1751623933256-ec9d264f356a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMHRlc3RpbmclMjBsYWJvcmF0b3J5fGVufDF8fHx8MTc2NjQ4NTg0M3ww&ixlib=rb-4.1.0&q=80&w=1080",
      values: {
        airContent: 4.2,
        unit: "%",
        target: "4-6",
        method: "Pressure method",
        status: "optimal",
        compliance: "IS 1199:1959"
      }
    },
    {
      id: "setting-time",
      name: "Initial Setting Time",
      category: "fresh",
      status: "completed",
      date: "Dec 23, 2024 - 10:15 AM",
      description: "Time required for concrete to begin hardening",
      imageUrl: "https://images.unsplash.com/photo-1585351738104-fce103eca1b1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMHNhbXBsZSUyMHRlc3Rpbmd8ZW58MXx8fHwxNzY2NDg1ODQ0fDA&ixlib=rb-4.1.0&q=80&w=1080",
      values: {
        initialSet: 145,
        finalSet: 285,
        unit: "minutes",
        target: "≥ 30 min",
        status: "optimal",
        compliance: "IS 4031:1988"
      }
    },
  ];

  const hardenedTests: TestRecord[] = [
    {
      id: "strength-7",
      name: "7-Day Cube Strength",
      category: "hardened",
      status: "completed",
      date: "Dec 30, 2024 - 10:15 AM",
      description: "Compressive strength at 7 days - early age strength",
      imageUrl: "https://images.unsplash.com/photo-1731317734784-49e5ac232322?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMGN1YmUlMjBjb21wcmVzc2lvbiUyMHRlc3R8ZW58MXx8fHwxNzY2NDg1ODQyfDA&ixlib=rb-4.1.0&q=80&w=1080",
      values: {
        strength: 24.2,
        unit: "MPa",
        target: "≥ 20",
        cubeWeight: 8250,
        cubeSize: "150mm",
        load: 544500,
        loadUnit: "N",
        area: 22500,
        status: "optimal",
        compliance: "IS 516:1959"
      }
    },
    {
      id: "strength-14",
      name: "14-Day Cube Strength",
      category: "hardened",
      status: "pending",
      date: "Scheduled for Jan 06, 2025",
      description: "Intermediate strength development check",
      imageUrl: "https://images.unsplash.com/photo-1731317734784-49e5ac232322?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMGN1YmUlMjBjb21wcmVzc2lvbiUyMHRlc3R8ZW58MXx8fHwxNzY2NDg1ODQyfDA&ixlib=rb-4.1.0&q=80&w=1080",
      values: {
        strength: 29.8,
        unit: "MPa",
        target: "≥ 25",
        cubeWeight: 8240,
        cubeSize: "150mm",
        load: 670500,
        loadUnit: "N",
        area: 22500,
        status: "optimal",
        compliance: "IS 516:1959"
      }
    },
    {
      id: "strength-28",
      name: "28-Day Cube Strength",
      category: "hardened",
      status: "pending",
      date: "Scheduled for Jan 20, 2025",
      description: "Standard characteristic strength test - final acceptance",
      imageUrl: "https://images.unsplash.com/photo-1731317734784-49e5ac232322?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMGN1YmUlMjBjb21wcmVzc2lvbiUyMHRlc3R8ZW58MXx8fHwxNzY2NDg1ODQyfDA&ixlib=rb-4.1.0&q=80&w=1080",
      values: {
        strength: 33.8,
        unit: "MPa",
        target: "≥ 30",
        cubeWeight: 8235,
        cubeSize: "150mm",
        load: 760500,
        loadUnit: "N",
        area: 22500,
        status: "optimal",
        compliance: "IS 516:1959"
      }
    },
    {
      id: "water-absorption",
      name: "Water Absorption Test",
      category: "hardened",
      status: "completed",
      date: "Jan 20, 2025 - 02:45 PM",
      description: "Measures porosity and durability of hardened concrete",
      imageUrl: "https://images.unsplash.com/photo-1751623933256-ec9d264f356a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMHRlc3RpbmclMjBsYWJvcmF0b3J5fGVufDF8fHx8MTc2NjQ4NTg0M3ww&ixlib=rb-4.1.0&q=80&w=1080",
      values: {
        absorption: 3.8,
        unit: "%",
        target: "< 5",
        dryWeight: 8150,
        wetWeight: 8460,
        status: "optimal",
        compliance: "IS 1124:1974"
      }
    },
    {
      id: "rebound-hammer",
      name: "Rebound Hammer Test",
      category: "hardened",
      status: "completed",
      date: "Jan 20, 2025 - 03:15 PM",
      description: "Non-destructive test for surface hardness",
      imageUrl: "https://images.unsplash.com/photo-1764835822981-1a3ccf47c369?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBxdWFsaXR5JTIwY29udHJvbHxlbnwxfHx8fDE3NjY0ODU4NDN8MA&ixlib=rb-4.1.0&q=80&w=1080",
      values: {
        reboundNumber: 42,
        unit: "R",
        readings: [40, 42, 43, 41, 42, 44, 41, 42, 43, 42],
        estimatedStrength: 35.2,
        status: "optimal",
        compliance: "IS 13311:1992"
      }
    },
    {
      id: "upv-test",
      name: "Ultrasonic Pulse Velocity",
      category: "hardened",
      status: "completed",
      date: "Jan 20, 2025 - 03:45 PM",
      description: "Assesses concrete quality and uniformity",
      imageUrl: "https://images.unsplash.com/photo-1585351738104-fce103eca1b1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMHNhbXBsZSUyMHRlc3Rpbmd8ZW58MXx8fHwxNzY2NDg1ODQ0fDA&ixlib=rb-4.1.0&q=80&w=1080",
      values: {
        velocity: 4680,
        unit: "m/s",
        quality: "Excellent",
        target: "> 4500",
        distance: 150,
        time: 32.05,
        status: "optimal",
        compliance: "IS 13311:1992"
      }
    },
  ];

  // Apply dynamic status to tests
  const getTestWithDynamicStatus = (test: TestRecord) => ({
    ...test,
    status: testStatuses[test.id] || test.status,
  });

  const currentTests = (activeCategory === "fresh" ? freshTests : hardenedTests).map(getTestWithDynamicStatus);
  const allTestsCompleted = [...freshTests, ...hardenedTests].every(t => (testStatuses[t.id] || t.status) === "completed");

  const toggleExpanded = (testId: string) => {
    setExpandedTest(expandedTest === testId ? null : testId);
  };

  const handleViewImage = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  const handleExportData = (testId: string) => {
    console.log("Exporting test data:", testId);
    setShowToast({ message: "Test data exported successfully", type: "success" });
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1B2A5B] to-[#0A0F1C] text-white px-5 md:px-8 lg:px-12 pt-8 pb-6">
        <div className="max-w-7xl mx-auto">
          <h1
            style={{
              fontSize: "clamp(28px, 6vw, 40px)",
              letterSpacing: "-0.03em",
              fontWeight: "800",
              marginBottom: "8px",
            }}
          >
            Quality Tests
          </h1>
          <p
            className="text-white/70"
            style={{
              fontSize: "14px",
              letterSpacing: "0.01em",
              fontWeight: "500",
            }}
          >
            Comprehensive testing workflow
          </p>
        </div>
      </div>

      {/* Test Cards Grid */}
      <div className="px-5 md:px-8 lg:px-12 pt-6 space-y-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentTests.map((test, index) => (
            <motion.div
              key={test.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08, duration: 0.5 }}
              className="bg-white rounded-lg overflow-hidden"
              style={{
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04), 0 12px 24px rgba(0, 0, 0, 0.06)",
              }}
            >
              {/* Test Image - Large Hero */}
              {test.imageUrl && (
                <div
                  onClick={() => handleViewImage(test.imageUrl!)}
                  className="w-full relative aspect-[16/10] overflow-hidden group cursor-pointer"
                >
                  <ImageWithFallback
                    src={test.imageUrl}
                    alt={test.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#00315c]/80 via-[#00315c]/30 to-transparent" />
                  
                  {/* Status Badge on Image */}
                  <div className="absolute top-4 right-4">
                    <div
                      className="px-4 py-2 rounded-full backdrop-blur-xl flex items-center gap-2"
                      style={{
                        backgroundColor: test.status === "completed" ? "rgba(0, 49, 92, 0.9)" : "rgba(0, 49, 92, 0.75)",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{
                          backgroundColor: "white",
                        }}
                      />
                      <span
                        className="text-white"
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                      >
                        {test.status}
                      </span>
                    </div>
                  </div>

                  {/* Tap to View Indicator */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div
                      className="text-white/90 flex items-center gap-2"
                      style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.01em" }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 2L14 14M14 2L2 14" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="1.5" opacity="0.5"/>
                      </svg>
                      Tap to enlarge
                    </div>
                  </div>
                </div>
              )}

              {/* Test Header - Clickable */}
              <button
                onClick={() => toggleExpanded(test.id)}
                className="w-full px-6 py-5 text-left"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3
                      className="text-[#00315c] mb-2"
                      style={{
                        fontSize: "20px",
                        fontWeight: "800",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {test.name}
                    </h3>
                    <p
                      className="text-gray-400 mb-2"
                      style={{ fontSize: "12px", letterSpacing: "0.01em", fontWeight: "500" }}
                    >
                      {test.description}
                    </p>
                    <p
                      className="text-[#00315c]"
                      style={{ fontSize: "11px", letterSpacing: "0.02em", fontWeight: "600", opacity: 0.7 }}
                    >
                      {test.date}
                    </p>
                  </div>
                  <motion.div
                    animate={{ rotate: expandedTest === test.id ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="ml-4"
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "#F6F7F9" }}
                    >
                      <svg
                        width="14"
                        height="8"
                        viewBox="0 0 14 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1.5L7 6.5L13 1.5"
                          stroke="#00315c"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </motion.div>
                </div>
              </button>

              {/* Test Data - Conditional Rendering */}
              {test.values && (
                <div className="px-6 pb-6">
                  {/* Hero Value Display */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="p-6 rounded-lg mb-4 cursor-pointer"
                    style={{
                      background: "linear-gradient(135deg, #F6F7F9 0%, #F3F4F6 100%)",
                    }}
                    onClick={() => {
                      const value = test.values.slump || test.values.strength || test.values.temperature || test.values.density || test.values.airContent || test.values.absorption || test.values.reboundNumber || test.values.velocity || test.values.initialSet;
                      try {
                        navigator.clipboard.writeText(`${value} ${test.values.unit}`);
                        setShowToast({ message: "Value copied to clipboard", type: "success" });
                      } catch (error) {
                        // Clipboard API blocked or not available - silently fail
                        console.log("Clipboard access not available");
                      }
                    }}
                  >
                    <div className="flex items-end gap-3 mb-2">
                      <div
                        className="text-[#00315c]"
                        style={{
                          fontSize: "56px",
                          fontWeight: "800",
                          letterSpacing: "-0.04em",
                          lineHeight: "1",
                        }}
                      >
                        {test.values.slump || test.values.strength || test.values.temperature || test.values.density || test.values.airContent || test.values.absorption || test.values.reboundNumber || test.values.velocity || test.values.initialSet}
                      </div>
                      <div
                        className="text-gray-400 pb-3"
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                        }}
                      >
                        {test.values.unit}
                      </div>
                    </div>
                    
                    {/* Target Range */}
                    <div className="flex items-center gap-2">
                      <div
                        className="px-3 py-1.5 rounded"
                        style={{
                          backgroundColor: test.values.status === "optimal" ? "rgba(0, 49, 92, 0.08)" : "rgba(0, 49, 92, 0.08)",
                          color: test.values.status === "optimal" ? "#00315c" : "#00315c",
                          fontSize: "11px",
                          fontWeight: "700",
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}
                      >
                        Target: {test.values.target} {test.values.unit}
                      </div>
                      <div
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded"
                        style={{
                          backgroundColor: test.values.status === "optimal" ? "rgba(0, 49, 92, 0.15)" : "rgba(220, 38, 38, 0.15)",
                        }}
                      >
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{
                            backgroundColor: test.values.status === "optimal" ? "#00315c" : "#DC2626",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "700",
                            color: test.values.status === "optimal" ? "#00315c" : "#DC2626",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {test.values.status}
                        </span>
                      </div>
                    </div>
                    
                    {/* Copy Indicator */}
                    <div
                      className="text-gray-400 mt-3 text-center"
                      style={{
                        fontSize: "10px",
                        letterSpacing: "0.05em",
                        opacity: 0.6,
                      }}
                    >
                      TAP TO COPY VALUE
                    </div>
                  </motion.div>

                  {/* Detailed Data Grid */}
                  <AnimatePresence>
                    {expandedTest === test.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="overflow-hidden"
                      >
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          {Object.entries(test.values)
                            .filter(([key]) => !["status", "unit", "target", "slump", "strength", "temperature", "density", "airContent", "absorption", "reboundNumber", "velocity", "initialSet"].includes(key))
                            .map(([key, value]) => (
                              <motion.button
                                key={key}
                                whileTap={{ scale: 0.96 }}
                                whileHover={{ backgroundColor: "#F3F4F6" }}
                                onClick={() => {
                                  try {
                                    navigator.clipboard.writeText(String(value));
                                    setShowToast({ 
                                      message: `${key.replace(/([A-Z])/g, " $1").trim()}: ${value} copied`, 
                                      type: "success" 
                                    });
                                  } catch (error) {
                                    // Clipboard API blocked or not available - silently fail
                                    console.log("Clipboard access not available");
                                  }
                                }}
                                className="p-4 rounded-lg text-left transition-colors"
                                style={{ backgroundColor: "#F6F7F9" }}
                              >
                                <p
                                  className="text-gray-400 mb-1.5"
                                  style={{
                                    fontSize: "10px",
                                    letterSpacing: "0.05em",
                                    textTransform: "uppercase",
                                    fontWeight: "600",
                                  }}
                                >
                                  {key.replace(/([A-Z])/g, " $1").trim()}
                                </p>
                                <p
                                  className="text-[#00315c]"
                                  style={{ fontSize: "15px", fontWeight: "700" }}
                                >
                                  {Array.isArray(value) ? value.join(", ") : String(value)}
                                </p>
                                <p
                                  className="text-gray-400 mt-1"
                                  style={{
                                    fontSize: "9px",
                                    letterSpacing: "0.03em",
                                    opacity: 0.5,
                                  }}
                                >
                                  Tap to copy
                                </p>
                              </motion.button>
                            ))}
                        </div>

                        {/* Compliance Badge */}
                        {test.values.compliance && (
                          <motion.button
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              setShowToast({ 
                                message: `Standard: ${test.values.compliance} - Test compliant`, 
                                type: "info" 
                              });
                            }}
                            className="w-full p-4 rounded-lg flex items-center gap-3 mb-4 text-left"
                            style={{
                              background: "linear-gradient(135deg, rgba(0, 49, 92, 0.05) 0%, rgba(0, 49, 92, 0.1) 100%)",
                              border: "1px solid rgba(0, 49, 92, 0.15)",
                            }}
                          >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10 2L12.5 7.5L18.5 8.5L14 13L15.5 19L10 16L4.5 19L6 13L1.5 8.5L7.5 7.5L10 2Z" fill="#00315c" fillOpacity="0.2" stroke="#00315c" strokeWidth="1.5"/>
                            </svg>
                            <div className="flex-1">
                              <p
                                className="text-gray-400"
                                style={{ fontSize: "10px", letterSpacing: "0.05em", fontWeight: "600", textTransform: "uppercase" }}
                              >
                                Compliance Standard
                              </p>
                              <p
                                className="text-[#00315c]"
                                style={{ fontSize: "13px", fontWeight: "700" }}
                              >
                                {test.values.compliance}
                              </p>
                            </div>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 12.5V8M8 5.5H8.01M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z" stroke="#00315c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </motion.button>
                        )}

                        {/* Primary Test Actions */}
                        {test.status === "pending" && (
                          <motion.button
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleStartTest(test.id)}
                            className="w-full mt-3 py-4 px-4 rounded flex items-center justify-center gap-2"
                            style={{
                              background: "linear-gradient(135deg, #00315c 0%, #001f3d 100%)",
                              color: "white",
                              fontSize: "14px",
                              fontWeight: "700",
                              boxShadow: "0 4px 12px rgba(0, 49, 92, 0.3)",
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M6.75 4.5L12.75 9L6.75 13.5V4.5Z" fill="white" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>Start Test</span>
                          </motion.button>
                        )}

                        {test.status === "in-progress" && (
                          <motion.button
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleCompleteTest(test.id)}
                            className="w-full mt-3 py-4 px-4 rounded flex items-center justify-center gap-2"
                            style={{
                              background: "linear-gradient(135deg, #00315c 0%, #001f3d 100%)",
                              color: "white",
                              fontSize: "14px",
                              fontWeight: "700",
                              boxShadow: "0 4px 12px rgba(0, 49, 92, 0.3)",
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15 4.5L6.75 12.75L3 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>Complete Test & Generate Report</span>
                          </motion.button>
                        )}

                        {/* Additional Quick Actions */}
                        <div className="grid grid-cols-3 gap-2 mt-3">
                          <motion.button
                            whileTap={{ scale: 0.96 }}
                            onClick={() => handleShareTest(test.id)}
                            className="py-3 px-3 rounded flex flex-col items-center gap-1.5"
                            style={{
                              backgroundColor: "#F6F7F9",
                              color: "#6B7280",
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 5.5C12.8284 5.5 13.5 4.82843 13.5 4C13.5 3.17157 12.8284 2.5 12 2.5C11.1716 2.5 10.5 3.17157 10.5 4C10.5 4.82843 11.1716 5.5 12 5.5Z" stroke="currentColor" strokeWidth="1.5"/>
                              <path d="M4 10C4.82843 10 5.5 9.32843 5.5 8.5C5.5 7.67157 4.82843 7 4 7C3.17157 7 2.5 7.67157 2.5 8.5C2.5 9.32843 3.17157 10 4 10Z" stroke="currentColor" strokeWidth="1.5"/>
                              <path d="M12 13.5C12.8284 13.5 13.5 12.8284 13.5 12C13.5 11.1716 12.8284 10.5 12 10.5C11.1716 10.5 10.5 11.1716 10.5 12C10.5 12.8284 11.1716 13.5 12 13.5Z" stroke="currentColor" strokeWidth="1.5"/>
                              <path d="M5.3 9.2L10.7 11.3M10.7 4.7L5.3 7.3" stroke="currentColor" strokeWidth="1.5"/>
                            </svg>
                            <span style={{ fontSize: "10px", fontWeight: "600" }}>Share</span>
                          </motion.button>
                          
                          <motion.button
                            whileTap={{ scale: 0.96 }}
                            onClick={() => {
                              window.print();
                              setShowToast({ message: "Preparing test for print...", type: "info" });
                            }}
                            className="py-3 px-3 rounded flex flex-col items-center gap-1.5"
                            style={{
                              backgroundColor: "#F6F7F9",
                              color: "#6B7280",
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 5.5V2.5H12V5.5M4 10.5H3C2.73478 10.5 2.48043 10.3946 2.29289 10.2071C2.10536 10.0196 2 9.76522 2 9.5V6.5C2 6.23478 2.10536 5.98043 2.29289 5.79289C2.48043 5.60536 2.73478 5.5 3 5.5H13C13.2652 5.5 13.5196 5.60536 13.7071 5.79289C13.8946 5.98043 14 6.23478 14 6.5V9.5C14 9.76522 13.8946 10.0196 13.7071 10.2071C13.5196 10.3946 13.2652 10.5 13 10.5H12M4 8.5H12V13.5H4V8.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span style={{ fontSize: "10px", fontWeight: "600" }}>Print</span>
                          </motion.button>

                          <motion.button
                            whileTap={{ scale: 0.96 }}
                            onClick={() => handleToggleFavorite(test.id)}
                            className="py-3 px-3 rounded flex flex-col items-center gap-1.5"
                            style={{
                              backgroundColor: favorites.includes(test.id) ? "rgba(0, 49, 92, 0.1)" : "#F6F7F9",
                              color: favorites.includes(test.id) ? "#00315c" : "#6B7280",
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill={favorites.includes(test.id) ? "#00315c" : "none"} xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 2L9.5 6L13.5 6.5L10.5 9.5L11.5 13.5L8 11.5L4.5 13.5L5.5 9.5L2.5 6.5L6.5 6L8 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span style={{ fontSize: "10px", fontWeight: "600" }}>
                              {favorites.includes(test.id) ? "Saved" : "Save"}
                            </span>
                          </motion.button>
                        </div>

                        {/* Slump Test Report Preview Button */}
                        {test.id === "slump" && (
                          <motion.button
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              setShowSlumpReportPreview(true);
                              setShowToast({ message: "Opening report preview...", type: "info" });
                            }}
                            className="w-full mt-3 py-4 px-4 rounded flex items-center justify-center gap-2"
                            style={{
                              background: "linear-gradient(135deg, #00315c 0%, #001f3d 100%)",
                              color: "white",
                              boxShadow: "0 4px 12px rgba(0, 49, 92, 0.24), 0 2px 4px rgba(0, 49, 92, 0.12)",
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2.25 9C2.25 9 4.875 3.75 9 3.75C13.125 3.75 15.75 9 15.75 9C15.75 9 13.125 14.25 9 14.25C4.875 14.25 2.25 9 2.25 9Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M9 11.25C10.2426 11.25 11.25 10.2426 11.25 9C11.25 7.75736 10.2426 6.75 9 6.75C7.75736 6.75 6.75 7.75736 6.75 9C6.75 10.2426 7.75736 11.25 9 11.25Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}>
                              Preview Report
                            </span>
                          </motion.button>
                        )}

                        {/* Strength Test Report Preview Button */}
                        {(test.id === "strength-7" || test.id === "strength-14" || test.id === "strength-28") && (
                          <motion.button
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              setSelectedStrengthTest(test.id);
                              setShowStrengthReportPreview(true);
                              setShowToast({ message: "Opening strength test report...", type: "info" });
                            }}
                            className="w-full mt-3 py-4 px-4 rounded flex items-center justify-center gap-2"
                            style={{
                              background: "linear-gradient(135deg, #00315c 0%, #001f3d 100%)",
                              color: "white",
                              boxShadow: "0 4px 12px rgba(0, 49, 92, 0.24), 0 2px 4px rgba(0, 49, 92, 0.12)",
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2.25 9C2.25 9 4.875 3.75 9 3.75C13.125 3.75 15.75 9 15.75 9C15.75 9 13.125 14.25 9 14.25C4.875 14.25 2.25 9 2.25 9Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M9 11.25C10.2426 11.25 11.25 10.2426 11.25 9C11.25 7.75736 10.2426 6.75 9 6.75C7.75736 6.75 6.75 7.75736 6.75 9C6.75 10.2426 7.75736 11.25 9 11.25Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}>
                              Preview Report
                            </span>
                          </motion.button>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Scheduled Test Buttons */}
      <div className="px-5 md:px-8 lg:px-12 pt-6 pb-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div
              className="text-[#717182]"
              style={{ fontSize: "11px", letterSpacing: "0.05em", fontWeight: "600" }}
            >
              SCHEDULED STRENGTH TESTS ({daysSinceCasting} days since casting)
            </div>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => handleSetReminder("")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl"
              style={{
                backgroundColor: "#3B82F6",
                color: "white",
                fontSize: "12px",
                fontWeight: "700",
                boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2.66667C13 1.93029 12.403 1.33333 11.6667 1.33333C10.9303 1.33333 10.3333 1.93029 10.3333 2.66667C10.3333 9.33333 8 10.6667 8 10.6667C8 10.6667 5.66667 9.33333 5.66667 2.66667C5.66667 1.93029 5.0697 1.33333 4.33333 1.33333C3.59695 1.33333 3 1.93029 3 2.66667C3 5.33333 2 12 2 12H14C14 12 13 5.33333 13 2.66667Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9.33333 12V13.3333C9.33333 14.0697 8.73638 14.6667 8 14.6667C7.26362 14.6667 6.66667 14.0697 6.66667 13.3333V12" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Set Reminder
            </motion.button>
          </div>

          {/* Active Reminders Display */}
          {activeReminders.length > 0 && (
            <div className="mb-4 space-y-2">
              {activeReminders.map((reminder) => (
                <motion.div
                  key={reminder.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between p-3 rounded-xl"
                  style={{
                    backgroundColor: "#FEF3C7",
                    border: "1px solid #F59E0B",
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "#F59E0B" }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 3.5V7L9.33333 8.16667" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7 1.16667C3.77833 1.16667 1.16667 3.77833 1.16667 7C1.16667 10.2217 3.77833 12.8333 7 12.8333C10.2217 12.8333 12.8333 10.2217 12.8333 7C12.8333 3.77833 10.2217 1.16667 7 1.16667Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div>
                      <p style={{ fontSize: "13px", fontWeight: "700", color: "#92400E" }}>
                        {reminder.testType}
                      </p>
                      <p style={{ fontSize: "11px", fontWeight: "500", color: "#92400E", opacity: 0.8 }}>
                        {reminder.reminderDate} {reminder.reminderTime && `at ${reminder.reminderTime}`}
                      </p>
                    </div>
                  </div>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDeleteReminder(reminder.id)}
                    className="w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.5)" }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 2L10 10M2 10L10 2" stroke="#92400E" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </motion.button>
                </motion.div>
              ))}
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 7-Day Test Button */}
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => handleAddTestClick("7-day")}
              disabled={!isTest7DayDue}
              className="p-6 rounded-3xl text-left relative overflow-hidden"
              style={{
                background: isTest7DayDue 
                  ? "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
                  : "linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)",
                boxShadow: isTest7DayDue 
                  ? "0 8px 24px rgba(245, 158, 11, 0.3)"
                  : "0 2px 8px rgba(0, 0, 0, 0.04)",
                opacity: isTest7DayDue ? 1 : 0.6,
              }}
            >
              {isTest7DayDue && (
                <div className="absolute top-3 right-3">
                  <div
                    className="w-3 h-3 rounded-full animate-pulse"
                    style={{ backgroundColor: "#FEF3C7" }}
                  />
                </div>
              )}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "800",
                      letterSpacing: "-0.02em",
                      color: isTest7DayDue ? "white" : "#0A0F1C",
                    }}
                  >
                    7-Day
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: isTest7DayDue ? "rgba(255,255,255,0.9)" : "#717182",
                    }}
                  >
                    Cube Strength Test
                  </div>
                </div>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4V20M12 4L8 8M12 4L16 8" stroke={isTest7DayDue ? "white" : "#717182"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div
                className="px-3 py-1.5 rounded-lg inline-block"
                style={{
                  backgroundColor: isTest7DayDue ? "rgba(255,255,255,0.2)" : "#1B2A5B10",
                  color: isTest7DayDue ? "white" : "#1B2A5B",
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "0.05em",
                }}
              >
                {isTest7DayDue ? "ADD TEST NOW" : `Available in ${7 - daysSinceCasting} days`}
              </div>
            </motion.button>

            {/* 14-Day Test Button */}
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => handleAddTestClick("14-day")}
              disabled={!isTest14DayDue}
              className="p-6 rounded-3xl text-left relative overflow-hidden"
              style={{
                background: isTest14DayDue 
                  ? "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)"
                  : "linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)",
                boxShadow: isTest14DayDue 
                  ? "0 8px 24px rgba(59, 130, 246, 0.3)"
                  : "0 2px 8px rgba(0, 0, 0, 0.04)",
                opacity: isTest14DayDue ? 1 : 0.6,
              }}
            >
              {isTest14DayDue && (
                <div className="absolute top-3 right-3">
                  <div
                    className="w-3 h-3 rounded-full animate-pulse"
                    style={{ backgroundColor: "#DBEAFE" }}
                  />
                </div>
              )}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "800",
                      letterSpacing: "-0.02em",
                      color: isTest14DayDue ? "white" : "#0A0F1C",
                    }}
                  >
                    14-Day
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: isTest14DayDue ? "rgba(255,255,255,0.9)" : "#717182",
                    }}
                  >
                    Cube Strength Test
                  </div>
                </div>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4V20M12 4L8 8M12 4L16 8" stroke={isTest14DayDue ? "white" : "#717182"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div
                className="px-3 py-1.5 rounded-lg inline-block"
                style={{
                  backgroundColor: isTest14DayDue ? "rgba(255,255,255,0.2)" : "#1B2A5B10",
                  color: isTest14DayDue ? "white" : "#1B2A5B",
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "0.05em",
                }}
              >
                {isTest14DayDue ? "ADD TEST NOW" : `Available in ${14 - daysSinceCasting} days`}
              </div>
            </motion.button>

            {/* 28-Day Test Button */}
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => handleAddTestClick("28-day")}
              disabled={!isTest28DayDue}
              className="p-6 rounded-3xl text-left relative overflow-hidden"
              style={{
                background: isTest28DayDue 
                  ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                  : "linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)",
                boxShadow: isTest28DayDue 
                  ? "0 8px 24px rgba(16, 185, 129, 0.3)"
                  : "0 2px 8px rgba(0, 0, 0, 0.04)",
                opacity: isTest28DayDue ? 1 : 0.6,
              }}
            >
              {isTest28DayDue && (
                <div className="absolute top-3 right-3">
                  <div
                    className="w-3 h-3 rounded-full animate-pulse"
                    style={{ backgroundColor: "#D1FAE5" }}
                  />
                </div>
              )}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "800",
                      letterSpacing: "-0.02em",
                      color: isTest28DayDue ? "white" : "#0A0F1C",
                    }}
                  >
                    28-Day
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: isTest28DayDue ? "rgba(255,255,255,0.9)" : "#717182",
                    }}
                  >
                    Final Strength Test
                  </div>
                </div>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4V20M12 4L8 8M12 4L16 8" stroke={isTest28DayDue ? "white" : "#717182"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div
                className="px-3 py-1.5 rounded-lg inline-block"
                style={{
                  backgroundColor: isTest28DayDue ? "rgba(255,255,255,0.2)" : "#1B2A5B10",
                  color: isTest28DayDue ? "white" : "#1B2A5B",
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "0.05em",
                }}
              >
                {isTest28DayDue ? "ADD TEST NOW" : `Available in ${28 - daysSinceCasting} days`}
              </div>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Complete All Tests CTA */}
      {allTestsCompleted && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="px-5 pt-6 pb-6"
        >
          <button
            onClick={onNavigateToReports}
            className="w-full p-6 rounded-3xl text-center"
            style={{
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              boxShadow: "0 8px 24px rgba(16, 185, 129, 0.3)",
            }}
          >
            <div className="flex items-center justify-center gap-3 mb-2">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span
                className="text-white"
                style={{ fontSize: "20px", fontWeight: "800", letterSpacing: "-0.01em" }}
              >
                All Tests Complete
              </span>
            </div>
            <p
              className="text-white/80"
              style={{ fontSize: "13px", fontWeight: "600" }}
            >
              Generate comprehensive quality report
            </p>
          </button>
        </motion.div>
      )}

      {/* Add Test Modal */}
      <AnimatePresence>
        {showAddTestModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#0A0F1C]/95 z-[100] flex items-center justify-center p-5"
            style={{ backdropFilter: "blur(20px)" }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              style={{
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
              }}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2
                    className="text-[#0A0F1C] mb-1"
                    style={{
                      fontSize: "28px",
                      fontWeight: "800",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Add {selectedTestType} Test Result
                  </h2>
                  <p
                    className="text-[#717182]"
                    style={{ fontSize: "13px", fontWeight: "500" }}
                  >
                    Batch ID: {batchId}
                  </p>
                </div>
                <button
                  onClick={() => setShowAddTestModal(false)}
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "#F9FAFB" }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 5L15 15M5 15L15 5" stroke="#0A0F1C" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {/* Form Fields */}
              <div className="space-y-5">
                {/* Cube Strength */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Compressive Strength (MPa) *
                  </label>
                  <input
                    type="number"
                    value={testFormData.strength}
                    onChange={(e) => setTestFormData({ ...testFormData, strength: e.target.value })}
                    placeholder="e.g., 24.2"
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  />
                </div>

                {/* Cube Weight */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Cube Weight (grams) *
                  </label>
                  <input
                    type="number"
                    value={testFormData.cubeWeight}
                    onChange={(e) => setTestFormData({ ...testFormData, cubeWeight: e.target.value })}
                    placeholder="e.g., 8250"
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  />
                </div>

                {/* Load Applied */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Load Applied (N)
                  </label>
                  <input
                    type="number"
                    value={testFormData.load}
                    onChange={(e) => setTestFormData({ ...testFormData, load: e.target.value })}
                    placeholder="e.g., 544500"
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  />
                </div>

                {/* Cube Dimensions */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Cube Dimensions (mm)
                  </label>
                  <input
                    type="text"
                    value={testFormData.cubeDimensions}
                    onChange={(e) => setTestFormData({ ...testFormData, cubeDimensions: e.target.value })}
                    placeholder="e.g., 150 x 150 x 150"
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  />
                </div>

                {/* Grid for Temperature and Humidity */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      className="text-[#0A0F1C] mb-2 block"
                      style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                    >
                      Temperature (°C)
                    </label>
                    <input
                      type="number"
                      value={testFormData.temperature}
                      onChange={(e) => setTestFormData({ ...testFormData, temperature: e.target.value })}
                      placeholder="e.g., 28"
                      className="w-full p-4 rounded-2xl border-2 focus:outline-none"
                      style={{
                        backgroundColor: "#F9FAFB",
                        borderColor: "#E5E7EB",
                        fontSize: "15px",
                        fontWeight: "600",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      className="text-[#0A0F1C] mb-2 block"
                      style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                    >
                      Humidity (%)
                    </label>
                    <input
                      type="number"
                      value={testFormData.humidity}
                      onChange={(e) => setTestFormData({ ...testFormData, humidity: e.target.value })}
                      placeholder="e.g., 65"
                      className="w-full p-4 rounded-2xl border-2 focus:outline-none"
                      style={{
                        backgroundColor: "#F9FAFB",
                        borderColor: "#E5E7EB",
                        fontSize: "15px",
                        fontWeight: "600",
                      }}
                    />
                  </div>
                </div>

                {/* Testing Machine */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Testing Machine / Equipment
                  </label>
                  <input
                    type="text"
                    value={testFormData.testingMachine}
                    onChange={(e) => setTestFormData({ ...testFormData, testingMachine: e.target.value })}
                    placeholder="e.g., CTM-3000 Compression Testing Machine"
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  />
                </div>

                {/* Technician Name */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Technician / Tested By
                  </label>
                  <input
                    type="text"
                    value={testFormData.technicianName}
                    onChange={(e) => setTestFormData({ ...testFormData, technicianName: e.target.value })}
                    placeholder="e.g., John Smith"
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  />
                </div>

                {/* Failure Mode */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Failure Mode / Pattern
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowFailureModeSheet(true)}
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none text-left flex items-center justify-between"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "15px",
                      fontWeight: "600",
                      color: testFormData.failureMode ? "#0A0F1C" : "#717182",
                    }}
                  >
                    <span>{testFormData.failureMode || "Select failure mode"}</span>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                {/* Age at Test (Auto-filled) */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Age at Test (Days)
                  </label>
                  <input
                    type="number"
                    value={testFormData.ageAtTest}
                    onChange={(e) => setTestFormData({ ...testFormData, ageAtTest: e.target.value })}
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                    readOnly
                  />
                </div>

                {/* Average Strength (for multiple cubes) */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Average Strength (if testing multiple cubes)
                  </label>
                  <input
                    type="number"
                    value={testFormData.averageStrength}
                    onChange={(e) => setTestFormData({ ...testFormData, averageStrength: e.target.value })}
                    placeholder="e.g., 24.5"
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  />
                </div>

                {/* Test Notes */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Test Notes & Observations
                  </label>
                  <textarea
                    value={testFormData.notes}
                    onChange={(e) => setTestFormData({ ...testFormData, notes: e.target.value })}
                    placeholder="Add any observations, remarks, or special conditions..."
                    rows={3}
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none resize-none"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-3 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Upload Test Image
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="test-image-upload"
                    />
                    <label
                      htmlFor="test-image-upload"
                      className="block cursor-pointer"
                    >
                      <div
                        className="p-8 rounded-2xl border-2 border-dashed text-center"
                        style={{
                          backgroundColor: "#F9FAFB",
                          borderColor: uploadedImage ? "#10B981" : "#E5E7EB",
                        }}
                      >
                        {uploadedImage ? (
                          <div className="space-y-3">
                            <img
                              src={uploadedImage}
                              alt="Uploaded test"
                              className="max-h-48 mx-auto rounded-xl"
                            />
                            <p
                              className="text-[#10B981]"
                              style={{ fontSize: "13px", fontWeight: "700" }}
                            >
                              ✓ Image uploaded successfully
                            </p>
                            <p
                              className="text-[#717182]"
                              style={{ fontSize: "11px", fontWeight: "500" }}
                            >
                              Click to change image
                            </p>
                          </div>
                        ) : (
                          <>
                            <svg
                              width="40"
                              height="40"
                              viewBox="0 0 40 40"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="mx-auto mb-3"
                            >
                              <path
                                d="M20 10V30M20 10L15 15M20 10L25 15M10 25V30C10 31.1046 10.8954 32 12 32H28C29.1046 32 30 31.1046 30 30V25"
                                stroke="#1B2A5B"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <p
                              className="text-[#0A0F1C] mb-1"
                              style={{ fontSize: "14px", fontWeight: "700" }}
                            >
                              Click to upload test image
                            </p>
                            <p
                              className="text-[#717182]"
                              style={{ fontSize: "12px", fontWeight: "500" }}
                            >
                              PNG, JPG up to 10MB
                            </p>
                          </>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3 mt-6">
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowAddTestModal(false)}
                  className="py-2.5 px-4 rounded-xl"
                  style={{
                    backgroundColor: "#F9FAFB",
                    color: "#717182",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSubmitTest}
                  className="py-2.5 px-4 rounded-xl"
                  style={{
                    background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                    color: "white",
                    fontSize: "13px",
                    fontWeight: "600",
                    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                  }}
                >
                  Save & Generate PDF
                </motion.button>
              </div>

              {/* PDF Generated Success */}
              {generatedPDF && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 rounded-2xl flex items-center justify-between"
                  style={{
                    background: "linear-gradient(135deg, #10B98105 0%, #10B98110 100%)",
                    border: "1px solid #10B98130",
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "#10B981" }}
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 10L9 12L13 8M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div>
                      <p
                        className="text-[#10B981]"
                        style={{ fontSize: "13px", fontWeight: "700" }}
                      >
                        PDF Report Generated
                      </p>
                      <p
                        className="text-[#717182]"
                        style={{ fontSize: "11px", fontWeight: "500" }}
                      >
                        Test data saved successfully
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={handleViewPDF}
                      className="px-4 py-2 rounded-xl"
                      style={{
                        backgroundColor: "#10B98110",
                        color: "#10B981",
                        fontSize: "12px",
                        fontWeight: "700",
                      }}
                    >
                      View
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={handleDownloadPDF}
                      className="px-4 py-2 rounded-xl"
                      style={{
                        backgroundColor: "#10B981",
                        color: "white",
                        fontSize: "12px",
                        fontWeight: "700",
                      }}
                    >
                      Download
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 bg-[#0A0F1C]/95 z-[100] flex items-center justify-center p-5"
            style={{ backdropFilter: "blur(20px)" }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              className="relative max-w-full max-h-full"
            >
              <ImageWithFallback
                src={selectedImage}
                alt="Test Image"
                className="max-w-full max-h-[85vh] rounded-3xl"
                style={{
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
                }}
              />
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-4 -right-4 w-14 h-14 rounded-full backdrop-blur-xl flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%)",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
                  color: "white",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 3L17 17M3 17L17 3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reminder Modal */}
      <AnimatePresence>
        {showReminderModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#0A0F1C]/95 z-[100] flex items-center justify-center p-5"
            style={{ backdropFilter: "blur(20px)" }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl p-6 w-full max-w-lg"
              style={{
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
              }}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2
                    className="text-[#0A0F1C] mb-1"
                    style={{
                      fontSize: "24px",
                      fontWeight: "800",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Set Test Reminder
                  </h2>
                  <p
                    className="text-[#717182]"
                    style={{ fontSize: "13px", fontWeight: "500" }}
                  >
                    Get notified when tests are due
                  </p>
                </div>
                <button
                  onClick={() => setShowReminderModal(false)}
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "#F9FAFB" }}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 5L15 15M5 15L15 5" stroke="#0A0F1C" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                {/* Test Type Selection */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Test Type *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowReminderTypeSheet(true)}
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none text-left flex items-center justify-between"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "15px",
                      fontWeight: "600",
                      color: reminderData.testType ? "#0A0F1C" : "#717182",
                    }}
                  >
                    <span>{reminderData.testType || "Select test type"}</span>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                {/* Reminder Date */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Reminder Date *
                  </label>
                  <input
                    type="date"
                    value={reminderData.reminderDate}
                    onChange={(e) => setReminderData({ ...reminderData, reminderDate: e.target.value })}
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  />
                </div>

                {/* Reminder Time */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Reminder Time (Optional)
                  </label>
                  <input
                    type="time"
                    value={reminderData.reminderTime}
                    onChange={(e) => setReminderData({ ...reminderData, reminderTime: e.target.value })}
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  />
                </div>

                {/* Notes */}
                <div>
                  <label
                    className="text-[#0A0F1C] mb-2 block"
                    style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.01em" }}
                  >
                    Notes (Optional)
                  </label>
                  <textarea
                    value={reminderData.notes}
                    onChange={(e) => setReminderData({ ...reminderData, notes: e.target.value })}
                    placeholder="Add reminder notes..."
                    rows={3}
                    className="w-full p-4 rounded-2xl border-2 focus:outline-none resize-none"
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderColor: "#E5E7EB",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  />
                </div>

                {/* Quick Date Presets */}
                <div>
                  <p
                    className="text-[#717182] mb-2"
                    style={{ fontSize: "11px", fontWeight: "600", letterSpacing: "0.05em" }}
                  >
                    QUICK PRESETS
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        const date = new Date(castingDate);
                        date.setDate(date.getDate() + 7);
                        setReminderData({ 
                          ...reminderData, 
                          reminderDate: date.toISOString().split('T')[0],
                          testType: "7-Day Cube Test"
                        });
                      }}
                      className="py-3 px-4 rounded-xl"
                      style={{
                        backgroundColor: "#F9FAFB",
                        fontSize: "12px",
                        fontWeight: "700",
                        color: "#1B2A5B",
                      }}
                    >
                      +7 Days
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        const date = new Date(castingDate);
                        date.setDate(date.getDate() + 14);
                        setReminderData({ 
                          ...reminderData, 
                          reminderDate: date.toISOString().split('T')[0],
                          testType: "14-Day Cube Test"
                        });
                      }}
                      className="py-3 px-4 rounded-xl"
                      style={{
                        backgroundColor: "#F9FAFB",
                        fontSize: "12px",
                        fontWeight: "700",
                        color: "#1B2A5B",
                      }}
                    >
                      +14 Days
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        const date = new Date(castingDate);
                        date.setDate(date.getDate() + 28);
                        setReminderData({ 
                          ...reminderData, 
                          reminderDate: date.toISOString().split('T')[0],
                          testType: "28-Day Cube Test"
                        });
                      }}
                      className="py-3 px-4 rounded-xl"
                      style={{
                        backgroundColor: "#F9FAFB",
                        fontSize: "12px",
                        fontWeight: "700",
                        color: "#1B2A5B",
                      }}
                    >
                      +28 Days
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3 mt-6">
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowReminderModal(false)}
                  className="py-2.5 px-4 rounded-xl"
                  style={{
                    backgroundColor: "#F9FAFB",
                    color: "#717182",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSaveReminder}
                  className="py-2.5 px-4 rounded-xl"
                  style={{
                    background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                    color: "white",
                    fontSize: "13px",
                    fontWeight: "600",
                    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                  }}
                >
                  Set Reminder
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Slump Test Report Preview Modal */}
      <AnimatePresence>
        {showSlumpReportPreview && (() => {
          // Get actual test data
          const slumpTest = freshTests.find(t => t.id === "slump");
          const temperatureTest = freshTests.find(t => t.id === "temperature");
          const densityTest = freshTests.find(t => t.id === "density");
          
          // Extract values with fallbacks
          const slumpValue = slumpTest?.values?.slump || 85;
          const temperature = temperatureTest?.values?.temperature || 28;
          const density = densityTest?.values?.density || 2450;
          const testDate = slumpTest?.date?.split(' - ')[0] || castingDate;
          const testTime = slumpTest?.date?.split(' - ')[1] || "09:42 AM";
          
          return (
            <SlumpTestReportPreview
              batchId={batchId}
              reportId={`SLUMP-RPT-${new Date().getFullYear()}-${batchId.split('-').pop()}`}
              testDate={testDate}
              testTime={testTime}
              slumpValue={slumpValue}
              temperature={temperature}
              density={density}
              grade={grade}
              location="Central Mixing Plant - Unit 2"
              weather={`Clear, ${temperatureTest?.values?.ambient || 31}°C ambient`}
              customerName={projectInfo.customerName}
              projectName={projectInfo.projectName}
              onClose={() => setShowSlumpReportPreview(false)}
            />
          );
        })()}
      </AnimatePresence>

      {/* Strength Test Report Preview Modal */}
      <AnimatePresence>
        {showStrengthReportPreview && selectedStrengthTest && (() => {
          // Get actual test data based on selected test
          const strengthTest = hardenedTests.find(t => t.id === selectedStrengthTest);
          
          if (!strengthTest) return null;

          // Extract test age from ID
          const testAge = selectedStrengthTest === "strength-7" ? 7 
                        : selectedStrengthTest === "strength-14" ? 14 
                        : 28;
          
          // Extract values with fallbacks
          const strength = strengthTest.values?.strength || 0;
          const cubeWeight = strengthTest.values?.cubeWeight || 8250;
          const cubeSize = strengthTest.values?.cubeSize || "150mm";
          const load = strengthTest.values?.load || 0;
          const testDate = strengthTest.date?.split(' - ')[0] || castingDate;
          
          return (
            <StrengthTestReportPreview
              batchId={batchId}
              reportId={`STR-${testAge}D-RPT-${new Date().getFullYear()}-${batchId.split('-').pop()}`}
              testDate={testDate}
              testAge={testAge}
              strength={strength}
              cubeWeight={cubeWeight}
              cubeSize={cubeSize}
              load={load}
              grade={grade}
              location="Central Mixing Plant - Unit 2"
              customerName={projectInfo.customerName}
              projectName={projectInfo.projectName}
              technicianName={projectInfo.technicianName}
              testingMachine="CTM-3000 Compression Testing Machine"
              onClose={() => {
                setShowStrengthReportPreview(false);
                setSelectedStrengthTest(null);
              }}
            />
          );
        })()}
      </AnimatePresence>

      {/* Failure Mode Bottom Sheet */}
      <AnimatePresence>
        {showFailureModeSheet && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFailureModeSheet(false)}
              className="fixed inset-0 bg-[#0A0F1C]/40 z-[80]"
              style={{ backdropFilter: "blur(4px)" }}
            />

            {/* Bottom Sheet */}
            <motion.div
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 bg-white z-[90] rounded-t-3xl overflow-hidden"
              style={{
                boxShadow: "0 -4px 24px rgba(0, 0, 0, 0.15)",
                maxWidth: "640px",
                margin: "0 auto",
              }}
            >
              {/* Handle Bar */}
              <div className="flex justify-center pt-3 pb-2">
                <div
                  className="w-10 h-1 rounded-full bg-[#E5E7EB]"
                  style={{ opacity: 0.6 }}
                />
              </div>

              {/* Sheet Header */}
              <div className="px-6 pt-2 pb-4">
                <h3
                  className="text-[#0A0F1C] text-center"
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    letterSpacing: "-0.01em",
                  }}
                >
                  Select Failure Mode
                </h3>
              </div>

              {/* Options */}
              <div className="px-4 pb-6 space-y-2">
                {[
                  { mode: "Cone", color: "#10B981", bgLight: "#D1FAE5" },
                  { mode: "Cone & Shear", color: "#3B82F6", bgLight: "#DBEAFE" },
                  { mode: "Cone & Split", color: "#8B5CF6", bgLight: "#EDE9FE" },
                  { mode: "Shear", color: "#F59E0B", bgLight: "#FEF3C7" },
                  { mode: "Columnar", color: "#EF4444", bgLight: "#FEE2E2" },
                ].map(({ mode, color, bgLight }) => (
                  <motion.button
                    key={mode}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setTestFormData({ ...testFormData, failureMode: mode });
                      setShowFailureModeSheet(false);
                    }}
                    className="w-full flex items-center gap-4 px-4 py-4 rounded-2xl bg-white hover:bg-[#F9FAFB] transition-all"
                    style={{
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                      border: `2px solid ${testFormData.failureMode === mode ? color : "rgba(0, 0, 0, 0.04)"}`,
                    }}
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ 
                        backgroundColor: testFormData.failureMode === mode ? color : bgLight,
                        color: testFormData.failureMode === mode ? "white" : color,
                      }}
                    >
                      {testFormData.failureMode === mode ? (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path
                            d="M16 6L7.5 14.5L4 11"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path
                            d="M10 3v14M3 10h14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <circle
                            cx="10"
                            cy="10"
                            r="7"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <div
                        className="text-[#0A0F1C]"
                        style={{ fontSize: "15px", fontWeight: "600" }}
                      >
                        {mode}
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Cancel Button */}
              <div className="px-4 pb-6">
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowFailureModeSheet(false)}
                  className="w-full py-3 rounded-xl"
                  style={{
                    backgroundColor: "#F9FAFB",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#717182",
                  }}
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Reminder Test Type Bottom Sheet */}
      <AnimatePresence>
        {showReminderTypeSheet && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowReminderTypeSheet(false)}
              className="fixed inset-0 bg-[#0A0F1C]/40 z-[80]"
              style={{ backdropFilter: "blur(4px)" }}
            />

            {/* Bottom Sheet */}
            <motion.div
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 bg-white z-[90] rounded-t-3xl overflow-hidden"
              style={{
                boxShadow: "0 -4px 24px rgba(0, 0, 0, 0.15)",
                maxWidth: "640px",
                margin: "0 auto",
                maxHeight: "80vh",
              }}
            >
              {/* Handle Bar */}
              <div className="flex justify-center pt-3 pb-2">
                <div
                  className="w-10 h-1 rounded-full bg-[#E5E7EB]"
                  style={{ opacity: 0.6 }}
                />
              </div>

              {/* Sheet Header */}
              <div className="px-6 pt-2 pb-4 border-b border-[#E5E7EB]">
                <h3
                  className="text-[#0A0F1C] text-center"
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    letterSpacing: "-0.01em",
                  }}
                >
                  Select Test Type
                </h3>
              </div>

              {/* Options */}
              <div className="px-4 py-6 space-y-2 overflow-y-auto" style={{ maxHeight: "60vh" }}>
                {[
                  { 
                    value: "Slump Test", 
                    label: "Slump Test", 
                    desc: "Fresh Concrete", 
                    color: "#06B6D4",
                    bgLight: "#CFFAFE"
                  },
                  { 
                    value: "7-Day Cube Test", 
                    label: "7-Day Cube Test", 
                    desc: "Strength Testing", 
                    color: "#10B981",
                    bgLight: "#D1FAE5"
                  },
                  { 
                    value: "14-Day Cube Test", 
                    label: "14-Day Cube Test", 
                    desc: "Strength Testing", 
                    color: "#3B82F6",
                    bgLight: "#DBEAFE"
                  },
                  { 
                    value: "21-Day Cube Test", 
                    label: "21-Day Cube Test", 
                    desc: "Strength Testing", 
                    color: "#8B5CF6",
                    bgLight: "#EDE9FE"
                  },
                  { 
                    value: "28-Day Cube Test", 
                    label: "28-Day Cube Test", 
                    desc: "Strength Testing", 
                    color: "#EF4444",
                    bgLight: "#FEE2E2"
                  },
                ].map((test) => (
                  <motion.button
                    key={test.value}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setReminderData({ ...reminderData, testType: test.value });
                      setShowReminderTypeSheet(false);
                    }}
                    className="w-full flex items-center gap-4 px-4 py-4 rounded-2xl bg-white hover:bg-[#F9FAFB] transition-all"
                    style={{
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                      border: `2px solid ${reminderData.testType === test.value ? test.color : "rgba(0, 0, 0, 0.04)"}`,
                    }}
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ 
                        backgroundColor: reminderData.testType === test.value ? test.color : test.bgLight,
                        color: reminderData.testType === test.value ? "white" : test.color,
                      }}
                    >
                      {reminderData.testType === test.value ? (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path
                            d="M16 6L7.5 14.5L4 11"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : test.value === "Slump Test" ? (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path
                            d="M10 2v16M6 6l4-4 4 4M6 14l4 4 4-4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <rect
                            x="4"
                            y="12"
                            width="3"
                            height="6"
                            rx="1"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                          />
                          <rect
                            x="8.5"
                            y="8"
                            width="3"
                            height="10"
                            rx="1"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                          />
                          <rect
                            x="13"
                            y="4"
                            width="3"
                            height="14"
                            rx="1"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <div
                        className="text-[#0A0F1C]"
                        style={{ fontSize: "15px", fontWeight: "600" }}
                      >
                        {test.label}
                      </div>
                      <div
                        className="text-[#717182]"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        {test.desc}
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Cancel Button */}
              <div className="px-4 pb-6 border-t border-[#E5E7EB] pt-4">
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowReminderTypeSheet(false)}
                  className="w-full py-3 rounded-xl"
                  style={{
                    backgroundColor: "#F9FAFB",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#717182",
                  }}
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-5 right-5 z-50"
          >
            <Toast
              message={showToast.message}
              type={showToast.type}
              onClose={() => setShowToast(null)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}